import { clientApi } from './client';

/**
 * Conservation des jetons de session.
 *
 * DEUX ECARTS DELIBERES PAR RAPPORT A L'APP UTILISATEUR, tous deux dans le sens de la
 * securite (§0.2, « la securite gagne ») :
 *
 * 1. **Le jeton de rafraichissement n'est PAS persiste.** L'app utilisateur le garde en
 *    `localStorage` pour que la session survive a un F5 — un confort qui se paie d'une
 *    exposition en cas de XSS. Ici, les deux jetons vivent en memoire : fermer l'onglet
 *    ferme la session. Sur un outil qui voit a travers toutes les entreprises, une session
 *    qui dort dans un navigateur oublie est un risque disproportionne face au confort
 *    d'eviter une reconnexion.
 *
 * 2. **Cle de stockage distincte, origine distincte.** L'administration tourne sur son
 *    propre domaine (§0.2) : elle ne peut donc de toute facon pas lire le stockage de
 *    l'app utilisateur. Le prefixe `astrolabe-admin:` ne sert qu'au cas ou les deux
 *    partageraient une origine en developpement.
 *
 * Consequence assumee : un rechargement de page redemande la connexion. C'est visible,
 * c'est un peu agacant, et c'est voulu.
 */

let jetonAcces: string | null = null;
let jetonRafraichissement: string | null = null;
let expireLe: number | null = null;

export function lireJetonAcces(): string | null {
  return jetonAcces;
}

export function ecrireJetons(acces: string, rafraichissement: string, expiration: string): void {
  jetonAcces = acces;
  jetonRafraichissement = rafraichissement;
  expireLe = new Date(expiration).getTime();
}

export function viderJetons(): void {
  jetonAcces = null;
  jetonRafraichissement = null;
  expireLe = null;
}

export function sessionOuverte(): boolean {
  return jetonAcces !== null;
}

/** Marge avant expiration : on rafraichit un peu avant plutot que de subir un 401. */
const MARGE_MS = 30_000;

let rafraichissementEnCours: Promise<string | null> | null = null;

/**
 * Renvoie un jeton d'acces valide, en le rafraichissant si besoin.
 *
 * Les appels concurrents partagent la meme promesse : dix requetes simultanees qui
 * decouvrent un jeton expire ne declenchent qu'un seul rafraichissement, sinon la rotation
 * cote serveur en invaliderait neuf.
 */
export async function obtenirJetonFrais(): Promise<string | null> {
  if (!jetonRafraichissement) return null;

  if (jetonAcces && expireLe && Date.now() < expireLe - MARGE_MS) {
    return jetonAcces;
  }

  rafraichissementEnCours ??= (async () => {
    try {
      const { data, error } = await clientApi.POST('/v1/auth/rafraichir', {
        body: { jetonRafraichissement: jetonRafraichissement as string },
      });

      if (error || !data) {
        viderJetons();
        return null;
      }

      ecrireJetons(data.jetonAcces ?? '', data.jetonRafraichissement ?? '', data.expireLe ?? '');
      return jetonAcces;
    } catch {
      viderJetons();
      return null;
    } finally {
      rafraichissementEnCours = null;
    }
  })();

  return rafraichissementEnCours;
}
